import React, { useState, useEffect } from 'react';
import { FaUserSlash, FaSearch, FaUserCircle, FaUserPlus, FaEdit, FaFileExcel, FaTimes } from 'react-icons/fa';
import * as XLSX from 'xlsx';

const TeamManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [selected, setSelected] = useState([]); // bulk selection
  const [showProfile, setShowProfile] = useState(null); // user object
  const [showAdd, setShowAdd] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [msg, setMsg] = useState('');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');

  // Bulk selection handlers
  const toggleSelect = (email) => setSelected(sel => sel.includes(email) ? sel.filter(e => e !== email) : [...sel, email]);
  const clearSelected = () => setSelected([]);

  // Fetch users from API
  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);

    try {
      // Get base URL
      let baseUrl = '';
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
      } else if (window.location.hostname === 'healthcare.fliphatmedia.com') {
        baseUrl = 'https://healthcare.fliphatmedia.com';
      }

      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'get' }),
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setUsers(result.users || []);
      } else {
        throw new Error(result.error || 'Failed to fetch users');
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setMsg('Failed to load team members. Using cached data if available.');

      // Fallback to localStorage
      const cachedUsers = JSON.parse(localStorage.getItem('users') || '[]');
      if (cachedUsers.length > 0) {
        setUsers(cachedUsers);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle user deletion
  const handleDeleteUser = async (email) => {
    if (!window.confirm(`Are you sure you want to delete ${email}?`)) return;

    try {
      // Get base URL
      let baseUrl = '';
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
      } else if (window.location.hostname === 'healthcare.fliphatmedia.com') {
        baseUrl = 'https://healthcare.fliphatmedia.com';
      }

      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'delete', email }),
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setUsers(users.filter(u => u.email !== email));
        setMsg(`User ${email} deleted successfully`);

        // Update localStorage cache
        const cachedUsers = JSON.parse(localStorage.getItem('users') || '[]');
        localStorage.setItem('users', JSON.stringify(cachedUsers.filter(u => u.email !== email)));
      } else {
        throw new Error(result.error || 'Failed to delete user');
      }
    } catch (err) {
      console.error('Failed to delete user:', err);
      setMsg(`Error: ${err.message || 'Failed to delete user'}`);
    }
  };

  // Handle role change
  const handleRoleChange = async (email, newRole) => {
    try {
      // Get base URL
      let baseUrl = '';
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
      } else if (window.location.hostname === 'healthcare.fliphatmedia.com') {
        baseUrl = 'https://healthcare.fliphatmedia.com';
      }

      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'update',
          user: { email, role: newRole }
        }),
        credentials: 'include'
      });

      const result = await response.json();

      if (result.success) {
        setUsers(users.map(u => u.email === email ? { ...u, role: newRole } : u));
        setMsg(`Updated ${email} to ${newRole} role`);

        // Update localStorage cache
        const cachedUsers = JSON.parse(localStorage.getItem('users') || '[]');
        localStorage.setItem('users', JSON.stringify(
          cachedUsers.map(u => u.email === email ? { ...u, role: newRole } : u)
        ));
      } else {
        throw new Error(result.error || 'Failed to update user role');
      }
    } catch (err) {
      console.error('Failed to update user role:', err);
      setMsg(`Error: ${err.message || 'Failed to update user role'}`);
    }
  };

  // Filter users based on search term and role filter
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      (user.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });
  
  // Get role badge style
  const getRoleBadgeStyle = (role) => {
    switch(role) {
      case 'admin':
        return 'bg-purple-100  text-purple-800  border-purple-200 ';
      case 'therapist':
        return 'bg-green-100  text-green-800  border-green-200 ';
      case 'receptionist':
        return 'bg-yellow-100  text-yellow-800  border-yellow-200 ';
      case 'viewer':
        return 'bg-blue-100  text-blue-800  border-blue-200 ';
      default:
        return 'bg-gray-100  text-gray-800  border-gray-200 ';
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(filteredUsers);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team');
    XLSX.writeFile(wb, `team_${Date.now()}.xlsx`);
    setMsg('Exported team to Excel.');
    setTimeout(() => setMsg(''), 3000);
  };

  
  return (
    <div className="mb-6 sm:mb-12 bg-white  p-3 sm:p-6 rounded-xl shadow-md border border-slate-300 ">
      <h2 className="text-xl sm:text-2xl font-bold mb-4 sm:mb-6 text-center text-purple-700 ">Team Management</h2>

      {/* Controls */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 mb-4 sm:mb-6 items-stretch sm:items-center">
        <div className="relative flex-grow min-w-[200px] order-1">
          <input
            type="text"
            placeholder="Search by name or email"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300  rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 w-full bg-white  text-slate-800 "
          />
          <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 " />
        </div>
        <div className="w-full sm:w-36 order-2">
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300  rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white  text-slate-800 "
          >
            <option value="all">All Roles</option>
            <option value="admin">Admin</option>
            <option value="therapist">Therapist</option>
            <option value="receptionist">Receptionist</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-2 justify-center sm:justify-start order-3 w-full sm:w-auto">
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-2 bg-green-100  hover:bg-green-200 :bg-green-950/50 text-green-800  px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm"><FaUserPlus /> Add Member</button>
          <button onClick={exportToExcel} className="flex items-center gap-2 bg-blue-100  hover:bg-blue-200 :bg-blue-950/50 text-blue-800  px-3 sm:px-4 py-2 rounded-lg font-semibold text-sm"><FaFileExcel /> Export</button>
          {selected.length > 0 && (
            <button onClick={clearSelected} className="bg-red-100  hover:bg-red-200 :bg-red-950/50 text-red-700  px-3 py-2 rounded text-xs font-semibold">Clear Selection ({selected.length})</button>
          )}
        </div>
      </div>

      {msg && <div className="mb-4 text-center text-green-600 ">{msg}</div>}

      {/* Results Count */}
      <div className="mb-4 text-xs sm:text-sm text-gray-600 ">
        Showing {filteredUsers.length} of {users.length} team members
      </div>

      {/* Team Members */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-8 bg-gray-50  rounded-lg border border-gray-200 ">
          <p className="text-gray-500 ">No team members match your search</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          {filteredUsers.map((user) => (
            <div 
              key={user.email} 
              className={`border border-gray-200  rounded-lg overflow-hidden hover:shadow-md transition-shadow duration-300 bg-white  relative ${selected.includes(user.email) ? 'ring-2 ring-purple-600 bg-purple-50/10' : ''}`}
              onClick={e => { if (e.target.classList.contains('select-checkbox')) return; setShowProfile(user); }}
            >
              {/* Bulk select checkbox */}
              <input
                type="checkbox"
                className="select-checkbox absolute top-4 right-4 w-5 h-5 accent-purple-600"
                checked={selected.includes(user.email)}
                onChange={e => { e.stopPropagation(); toggleSelect(user.email); }}
                onClick={e => e.stopPropagation()}
              />
              <div className="bg-gradient-to-r from-purple-500 to-purple-700 p-4 text-white">
                <div className="flex items-center gap-3">
                  {user.avatar || user.avatarUrl ? (
                    <img src={user.avatar || user.avatarUrl} alt="avatar" className="w-10 h-10 rounded-full object-cover border-2 border-white" />
                  ) : (
                    <FaUserCircle className="text-3xl" />
                  )}
                  <div>
                    <h3 className="font-bold">{user.name}</h3>
                    <p className="text-sm text-purple-100">{user.email}</p>
                  </div>
                </div>
              </div>

              <div className="p-4">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500 ">Status</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium border ${
                    user.status === 'approved' || user.status === 'active'
                      ? 'bg-green-100  text-green-800  border-green-200 '
                      : user.status === 'suspended' || user.status === 'inactive'
                      ? 'bg-red-100  text-red-800  border-red-200 '
                      : 'bg-yellow-100  text-yellow-800  border-yellow-200 '
                  }`}>
                    {user.status}
                  </span>
                </div>
                <div className="flex justify-between items-center mb-4">
                  <span className="text-sm text-gray-500 ">Role</span>
                  <select
                    value={user.role}
                    onClick={e => e.stopPropagation()}
                    onChange={e => { e.stopPropagation(); handleRoleChange(user.email, e.target.value); }}
                    className={`px-3 py-1 rounded-full text-xs font-medium border bg-transparent text-slate-800  focus:outline-none cursor-pointer ${getRoleBadgeStyle(user.role)}`}
                  >
                    <option value="admin">Admin</option>
                    <option value="therapist">Therapist</option>
                    <option value="receptionist">Receptionist</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
                <div className="flex gap-2 justify-between">
                  <button
                    onClick={e => { e.stopPropagation(); setEditUser(user); }}
                    className="flex items-center gap-1 bg-blue-100  hover:bg-blue-200 :bg-blue-950/50 text-blue-800  px-3 py-1 rounded-lg text-sm transition-colors duration-300"
                  >
                    <FaEdit /> Edit
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); handleDeleteUser(user.email); }}
                    className="flex items-center gap-1 bg-red-100  hover:bg-red-200 :bg-red-950/50 text-red-800  px-3 py-1 rounded-lg text-sm transition-colors duration-300 disabled:opacity-30 disabled:bg-gray-100 :bg-slate-900 disabled:text-gray-400 :text-slate-650"
                    disabled={user.role === 'admin' || user.email === currentUser.email}
                  >
                    <FaUserSlash />
                    <span>Remove</span>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="mt-4 sm:mt-6 flex flex-col sm:flex-row flex-wrap gap-2 sm:gap-3 items-start sm:items-center">
          <span className="font-semibold text-sm w-full sm:w-auto">Bulk actions for {selected.length} selected:</span>
          <div className="flex flex-wrap gap-2 w-full sm:w-auto">
            <button
              className="bg-purple-100  hover:bg-purple-200 :bg-purple-950/50 text-purple-800  px-3 py-2 rounded text-xs font-semibold"
              onClick={() => { selected.forEach(email => handleRoleChange(email, 'therapist')); clearSelected(); setMsg('Changed role to therapist for selected.'); setTimeout(()=>setMsg(''),2000); }}
            >Set as Therapist</button>
            <button
              className="bg-blue-100  hover:bg-blue-200 :bg-blue-950/50 text-blue-800  px-3 py-2 rounded text-xs font-semibold"
              onClick={() => { selected.forEach(email => handleRoleChange(email, 'viewer')); clearSelected(); setMsg('Changed role to viewer for selected.'); setTimeout(()=>setMsg(''),2000); }}
            >Set as Viewer</button>
            <button
              className="bg-red-100  hover:bg-red-200 :bg-red-950/50 text-red-750  px-3 py-2 rounded text-xs font-semibold"
              onClick={() => { selected.forEach(email => handleDeleteUser(email)); clearSelected(); setMsg('Deleted selected users.'); setTimeout(()=>setMsg(''),2000); }}
            >Remove Selected</button>
          </div>
        </div>
      )}

      {/* Profile Modal */}
      {showProfile && (
        <UserProfileModal user={showProfile} onClose={() => setShowProfile(null)} />
      )}
      {/* Add/Edit Modal */}
      {(showAdd || editUser) && (
        <AddEditUserModal 
          user={editUser} 
          onClose={() => { setShowAdd(false); setEditUser(null); }} 
          onMsg={m => { setMsg(m); setTimeout(()=>setMsg(''),2000); }}
        />
      )}
    </div>
  );
};

// --- User Profile Modal ---
function UserProfileModal({ user, onClose }) {
  // Show all fields, last login, registration, etc.
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white  border border-slate-300  rounded-lg shadow-xl p-8 max-w-md w-full relative my-auto text-slate-800  animate-fadeIn" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="absolute top-2 right-2 text-gray-400  hover:text-red-500" onClick={onClose}><FaTimes /></button>
        <div className="flex flex-col items-center mb-4">
          {user.avatar || user.avatarUrl ? (
            <img src={user.avatar || user.avatarUrl} alt="avatar" className="w-20 h-20 rounded-full object-cover border-2 border-purple-400 mb-2" />
          ) : (
            <FaUserCircle className="text-5xl text-purple-400 mb-2" />
          )}
          <h2 className="font-bold text-xl mb-1">{user.name}</h2>
          <div className="text-gray-500 ">{user.email}</div>
        </div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Role:</span> {user.role}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Status:</span> {user.status}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Phone:</span> {user.phone || '—'}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Date of Birth:</span> {user.dob || '—'}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Gender:</span> {user.gender || '—'}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Address:</span> {user.address || '—'}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Preferred Contact:</span> {user.preferredContact || '—'}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Registered:</span> {user.createdAt ? new Date(user.createdAt).toLocaleString() : (user.id ? new Date(user.id).toLocaleString() : '—')}</div>
        <div className="mb-2"><span className="font-semibold text-slate-850 ">Last Login:</span> {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : '—'}</div>
        {/* Show appointments handled if therapist */}
        {user.role === 'therapist' && (
          <TherapistAppointments email={user.email} />
        )}
      </div>
    </div>
  );
}

// --- Therapist Appointments ---
function TherapistAppointments({ email }) {
  const appts = JSON.parse(localStorage.getItem('appointments') || '[]').filter(a => a.therapist === email);
  if (appts.length === 0) return <div className="mt-4 text-xs text-gray-500 ">No appointments handled yet.</div>;
  return (
    <div className="mt-4">
      <div className="font-semibold mb-1">Appointments Handled:</div>
      <ul className="max-h-32 overflow-auto text-xs ">
        {appts.map(a => (
          <li key={a.id} className="mb-1">{a.date} {a.time} — {a.service} ({a.status})</li>
        ))}
      </ul>
    </div>
  );
}

// --- Add/Edit User Modal ---
function AddEditUserModal({ user, onClose, onMsg }) {
  const isEdit = !!user;
  const [form, setForm] = useState(user || { name: '', email: '', phone: '', dob: '', gender: '', address: '', preferredContact: '', role: 'viewer', status: 'approved', avatar: '', avatarUrl: '' });
  const [avatarPreview, setAvatarPreview] = useState(user ? (user.avatar || user.avatarUrl || '') : '');
  const [warning, setWarning] = useState('');
  const [saving, setSaving] = useState(false);
  const [initialPassword, setInitialPassword] = useState('');
  function handleChange(e) {
    const { name, value, type, files } = e.target;
    if (type === 'file') {
      const file = files[0];
      if (file) {
        if (file.size > 100 * 1024) {
          setWarning('Avatar image is too large (max 100KB). It will not be saved.');
          setForm(f => ({ ...f, avatar: '' }));
          setAvatarPreview('');
          return;
        } else {
          setWarning('');
        }
        const reader = new FileReader();
        reader.onload = ev => {
          setForm(f => ({ ...f, avatar: ev.target.result }));
          setAvatarPreview(ev.target.result);
        };
        reader.onloadend = () => {};
        reader.readAsDataURL(file);
      }
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  }
  async function handleSubmit(e) {
    e.preventDefault();
    setSaving(true);
    setWarning('');
    setInitialPassword('');
    
    try {
      // Get base URL
      let baseUrl = '';
      if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
        baseUrl = `http://${window.location.hostname}${window.location.port ? ':' + window.location.port : ''}`;
      } else if (window.location.hostname === 'healthcare.fliphatmedia.com') {
        baseUrl = 'https://healthcare.fliphatmedia.com';
      }
      
      const action = isEdit ? 'update' : 'add';
      
      const response = await fetch(`${baseUrl}/api/manage_users.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          action: action, 
          user: form 
        }),
        credentials: 'include'
      });
      
      const result = await response.json();
      
      if (result.success) {
        // Update localStorage cache as well
        let users = JSON.parse(localStorage.getItem('users') || '[]');
        
        if (isEdit) {
          users = users.map(u => u.email === user.email ? { ...u, ...form } : u);
          onMsg('User updated successfully!');
        } else {
          const newUser = { 
            ...form, 
            id: result.userId || Date.now() 
          };
          users.push(newUser);
          
          if (result.initialPassword) {
            setInitialPassword(result.initialPassword);
          }
          
          onMsg('User added successfully!');
        }
        
        localStorage.setItem('users', JSON.stringify(users));
        
        if (!initialPassword) {
          setTimeout(onClose, 1500);
        }
      } else {
        throw new Error(result.error || `Failed to ${isEdit ? 'update' : 'add'} user`);
      }
    } catch (err) {
      console.error(`Failed to ${isEdit ? 'update' : 'add'} user:`, err);
      setWarning(err.message || `Failed to ${isEdit ? 'update' : 'add'} user`);
      onMsg(`Error: ${err.message || `Failed to ${isEdit ? 'update' : 'add'} user`}`);
    } finally {
      setSaving(false);
    }
  }
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm overflow-y-auto py-8">
      <form onSubmit={handleSubmit} className="bg-white  border border-slate-300  rounded-lg shadow-xl p-8 max-w-md w-full relative my-auto text-slate-800  animate-fadeIn" encType="multipart/form-data" style={{ maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="absolute top-2 right-2 text-gray-400  hover:text-red-500" type="button" onClick={onClose}><FaTimes /></button>
        <h2 className="font-bold text-xl mb-4">{isEdit ? 'Edit Team Member' : 'Add New Team Member'}</h2>
        <div className="mb-3 flex flex-col items-center">
          <label className="block text-xs font-semibold mb-1">Profile Picture</label>
          <div className="mb-2">
            <input type="file" accept="image/*" name="avatar" onChange={handleChange} className="hidden" id="avatar-input-modal" />
            <label htmlFor="avatar-input-modal" className="cursor-pointer inline-flex items-center px-3 py-2 bg-purple-100  hover:bg-purple-200 :bg-purple-950/50 text-purple-700  rounded shadow">
              {avatarPreview ? (
                <img src={avatarPreview} alt="Avatar" className="w-12 h-12 rounded-full object-cover border-2 border-purple-400" />
              ) : (
                <span>Upload Avatar</span>
              )}
            </label>
          </div>
          {warning && <div className="mb-2 text-xs text-yellow-750 ">{warning}</div>}
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Name</label>
          <input name="name" value={form.name} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none" required />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Email</label>
          <input name="email" value={form.email} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-850  focus:outline-none" required disabled={isEdit} />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Phone</label>
          <input name="phone" value={form.phone} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none" />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Date of Birth</label>
          <input name="dob" type="date" value={form.dob || ''} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none" />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Gender</label>
          <select name="gender" value={form.gender || ''} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none">
            <option value="">Select gender</option>
            <option value="male">Male</option>
            <option value="female">Female</option>
            <option value="other">Other</option>
            <option value="prefer-not">Prefer not to say</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Address</label>
          <input name="address" value={form.address || ''} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none" />
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Preferred Contact</label>
          <select name="preferredContact" value={form.preferredContact || ''} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none">
            <option value="">Select</option>
            <option value="email">Email</option>
            <option value="phone">Phone</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="sms">SMS</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Role</label>
          <select name="role" value={form.role} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none">
            <option value="admin">Admin</option>
            <option value="therapist">Therapist</option>
            <option value="receptionist">Receptionist</option>
            <option value="viewer">Viewer</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Status</label>
          <select name="status" value={form.status} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none">
            <option value="approved">Approved</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div className="mb-3">
          <label className="block text-xs font-semibold mb-1">Avatar URL</label>
          <input name="avatarUrl" value={form.avatarUrl} onChange={handleChange} className="w-full border border-gray-300  px-3 py-2 rounded bg-white  text-slate-800  focus:outline-none" />
        </div>
        {initialPassword && (
          <div className="mb-4 p-4 bg-green-50  border border-green-200  rounded text-green-800 ">
            <p className="font-bold mb-1">New user created!</p>
            <p className="text-sm">Initial password: <span className="font-mono font-bold">{initialPassword}</span></p>
            <p className="text-xs mt-2">Please share this password with the user. They will be asked to change it on first login.</p>
            <button 
              type="button" 
              onClick={onClose} 
              className="mt-3 w-full bg-green-600 hover:bg-green-700 text-white py-2 rounded font-semibold"
            >
              Done
            </button>
          </div>
        )}
        
        {warning && (
          <div className="mb-4 p-3 bg-red-50  border border-red-200  rounded text-red-700  text-sm">
            {warning}
          </div>
        )}
        
        <button type="submit" className="w-full bg-purple-600 hover:bg-purple-700 text-white py-2 rounded font-semibold mt-2" disabled={saving}>{saving ? 'Saving...' : (isEdit ? 'Update' : 'Add')}</button>
      </form>
    </div>
  );
}

export default TeamManagement;
